/* ============================================================
   Agri Solsec — Solar Pump Sizing & Quote Calculator
   Used by: sections/pump-calculator.liquid

   Sizing model
   ------------
   Total head = borehole depth + rise to tank + pipe friction.
   Pipe friction uses Hazen-Williams over the full pipe run
   (depth + delivery distance + rise):

       hf = 10.67 * Q^1.852 / (C^1.852 * D^4.8704) * L

   where Q is m3/s, D is internal pipe diameter in metres,
   C is the roughness coefficient and L the run length in metres.

   Each pump carries a performance curve of [flow m3/h, head m]
   points. We interpolate along it to find what the pump actually
   delivers at the customer's total head, then match that against
   the requested litres/hour.
   ============================================================ */

(function () {
  'use strict';

  var HAZEN_WILLIAMS_C = 150;

  /* Internal diameter (mm) for each nominal HDPE/PVC pipe size */
  var PIPE_INTERNAL_DIAMETER = {
    25: 20.4,
    32: 26.5,
    40: 33,
    50: 41.5,
    63: 52,
    75: 62,
    90: 74,
  };

  /* ---------- number helpers ---------- */

  function toNumber(value) {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return 0;
    var cleaned = String(value).replace(/[^0-9.\-]/g, '');
    var parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  /* "3500" -> "R3 500,00" (South African convention) */
  function formatRand(value) {
    var parts = Number(value || 0).toFixed(2).split('.');
    return 'R' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + parts[1];
  }

  /* "1500" -> "1 500" */
  function formatInt(value) {
    return Math.round(value || 0)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  /* ---------- hydraulics ---------- */

  function frictionHead(litresPerHour, internalDiameterMm, roughness, runLengthM) {
    if (litresPerHour <= 0 || internalDiameterMm <= 0 || runLengthM <= 0) return 0;
    var flowM3s = litresPerHour / 1000 / 3600;
    var diameterM = internalDiameterMm / 1000;
    return (
      ((10.67 * Math.pow(flowM3s, 1.852)) /
        (Math.pow(roughness, 1.852) * Math.pow(diameterM, 4.8704))) *
      runLengthM
    );
  }

  /* Flow (m3/h) a pump delivers at a given head, read off its curve */
  function flowAtHead(curve, head) {
    if (!curve || curve.length < 2) return 0;
    var first = curve[0];
    var last = curve[curve.length - 1];

    if (head >= first[1]) return 0;
    if (head <= last[1]) return last[0];

    for (var i = 0; i < curve.length - 1; i++) {
      var upper = curve[i];
      var lower = curve[i + 1];
      if (head <= upper[1] && head >= lower[1]) {
        var ratio = (upper[1] - head) / (upper[1] - lower[1]);
        return upper[0] + ratio * (lower[0] - upper[0]);
      }
    }
    return 0;
  }

  /*
    Borehole cable is sized in 10 m depth bands: the pump's cable table
    lists the mm2 required for 0-10 m, 10-20 m, and so on.
  */
  function autoCableSize(table, runLengthM) {
    if (!table || !table.length) return null;
    var index = Math.ceil((runLengthM || 0) / 10) - 1;
    if (index < 0) index = 0;
    if (index > table.length - 1) index = table.length - 1;
    return table[index];
  }

  /* ---------- parsing of theme-editor settings ---------- */

  /* "0:56, 0.3:50, 0.6:40" -> [[0,56],[0.3,50],[0.6,40]] */
  function parseCurve(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        if (bits.length !== 2) return null;
        return [toNumber(bits[0]), toNumber(bits[1])];
      })
      .filter(Boolean);
  }

  /* "2.5,2.5,4,4,6" -> [2.5,2.5,4,4,6] */
  function parseList(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (item) {
        return toNumber(item);
      })
      .filter(function (n) {
        return n > 0;
      });
  }

  /* "Gauteng|4.8|7" per line */
  function parseProvinces(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/\r?\n/)
      .map(function (line) {
        var bits = line.split('|');
        if (bits.length < 3) return null;
        var name = bits[0].trim();
        if (!name) return null;
        return { name: name, winter: toNumber(bits[1]), summer: toNumber(bits[2]) };
      })
      .filter(Boolean);
  }

  /* "2.5|30" per line — blank price means price on request */
  function parseCableOptions(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/\r?\n/)
      .map(function (line) {
        var bits = line.split('|');
        if (!bits[0] || !bits[0].trim()) return null;
        var size = toNumber(bits[0]);
        if (!size) return null;
        var priceText = bits.length > 1 ? bits[1].trim() : '';
        return { size: size, price: priceText === '' ? null : toNumber(priceText) };
      })
      .filter(Boolean);
  }

  /* ============================================================
     Calculator instance
     ============================================================ */

  function initCalculator(root) {
    if (!root || root.dataset.pumpCalcReady === 'true') return;
    root.dataset.pumpCalcReady = 'true';

    /* ---------- read data injected by Liquid ---------- */

    var configNode = root.querySelector('[data-pump-config]');
    if (!configNode) return;

    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      return;
    }

    var pumps = [];
    root.querySelectorAll('[data-pump-item]').forEach(function (node) {
      try {
        var pump = JSON.parse(node.textContent);
        pump.curve = parseCurve(pump.curve);
        pump.cableTable = parseList(pump.cableTable);
        if (pump.model && pump.curve.length >= 2) pumps.push(pump);
      } catch (error) {
        /* skip malformed block */
      }
    });

    if (!pumps.length) return;

    /* Cheapest first — the original app ranks by price, not capacity */
    pumps.sort(function (a, b) {
      return a.price - b.price;
    });

    var provinces = parseProvinces(config.provinces);
    var cableOptions = parseCableOptions(config.cableOptions);
    var panelOptions = [
      { watt: config.panelAWatt, price: config.panelAPrice },
      { watt: config.panelBWatt, price: config.panelBPrice },
    ].filter(function (option) {
      return option.watt > 0;
    });

    var maxPumpHead = Math.max.apply(
      null,
      pumps.map(function (pump) {
        return pump.maxHead;
      })
    );

    /* ---------- element lookup ---------- */

    function field(name) {
      return root.querySelector('[data-field="' + name + '"]');
    }

    function out(name) {
      return root.querySelector('[data-out="' + name + '"]');
    }

    function setText(name, text) {
      var node = out(name);
      if (node) node.textContent = text;
    }

    function show(node, visible) {
      if (node) node.hidden = !visible;
    }

    /* ---------- state the user controls beyond raw inputs ---------- */

    var state = {
      selectedModel: null,
      manualPick: false,
      showAllPumps: false,
      panelWatt: null,
      panelCount: null,
      cableSizeOverride: null,
      pumpQty: 1,
    };

    /* ---------- populate selects ---------- */

    var pipeSelect = field('pipe');
    Object.keys(PIPE_INTERNAL_DIAMETER)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (size) {
        var option = document.createElement('option');
        option.value = String(size);
        option.textContent = size + ' mm';
        if (size === toNumber(config.defaultPipe)) option.selected = true;
        pipeSelect.appendChild(option);
      });

    var provinceSelect = field('province');
    provinces.forEach(function (province) {
      var option = document.createElement('option');
      option.value = province.name;
      option.textContent = province.name;
      if (province.name === config.defaultProvince) option.selected = true;
      provinceSelect.appendChild(option);
    });

    var panelSelect = field('panelWatt');
    panelOptions.forEach(function (option) {
      var node = document.createElement('option');
      node.value = String(option.watt);
      node.textContent = option.watt + 'W · ' + formatRand(option.price).replace(',00', '');
      panelSelect.appendChild(node);
    });

    var cableSelect = field('cableSize');
    cableOptions.forEach(function (option) {
      var node = document.createElement('option');
      node.value = String(option.size);
      node.textContent =
        option.size +
        ' mm² · ' +
        (option.price === null ? 'POR' : 'R' + option.price + '/m');
      cableSelect.appendChild(node);
    });

    field('solarCableLength').value = String(config.solarCableDefaultM);

    /* ---------- the computation ---------- */

    function compute() {
      var depth = toNumber(field('depth').value);
      var distance = toNumber(field('distance').value);
      var rise = toNumber(field('rise').value);
      var litresPerHour = toNumber(field('litres').value);
      var pipeSize = toNumber(pipeSelect.value);

      var demandM3h = litresPerHour / 1000;
      var pipeRun = depth + distance + rise;
      var friction = frictionHead(
        litresPerHour,
        PIPE_INTERNAL_DIAMETER[pipeSize] || 0,
        HAZEN_WILLIAMS_C,
        pipeRun
      );
      var calculatedHead = depth + rise + friction;

      var headOverrideRaw = field('headOverride').value;
      var totalHead = headOverrideRaw !== '' ? toNumber(headOverrideRaw) : calculatedHead;

      /* Only meaningful once we know both demand and head */
      var isSized = demandM3h > 0 && totalHead > 0;

      var evaluated = pumps.map(function (pump) {
        var delivered = isSized ? flowAtHead(pump.curve, totalHead) : 0;
        var status = 'ok';
        var reason = '';

        if (isSized) {
          if (totalHead >= pump.maxHead) {
            status = 'no';
            reason = 'Head beyond this pump';
          } else if (delivered >= demandM3h) {
            status = delivered < demandM3h * 1.08 ? 'tight' : 'ok';
            reason = status === 'tight' ? 'Only just enough — confirm on curve' : 'Covers demand';
          } else {
            status = 'short';
            reason = 'Delivers less than needed';
          }
        }

        return {
          pump: pump,
          delivered: delivered,
          status: status,
          reason: reason,
        };
      });

      var capable = evaluated.filter(function (entry) {
        return entry.status === 'ok' || entry.status === 'tight';
      });

      /* Best fit: cheapest comfortable pump, else cheapest that just copes */
      var bestFit = null;
      if (isSized) {
        bestFit =
          capable.filter(function (entry) {
            return entry.status === 'ok';
          })[0] ||
          capable[0] ||
          null;
      }

      return {
        depth: depth,
        distance: distance,
        rise: rise,
        litresPerHour: litresPerHour,
        demandM3h: demandM3h,
        pipeSize: pipeSize,
        pipeRun: pipeRun,
        friction: friction,
        calculatedHead: calculatedHead,
        totalHead: totalHead,
        isSized: isSized,
        evaluated: evaluated,
        capable: capable,
        bestFit: bestFit,
      };
    }

    /* ---------- quote maths for the chosen pump ---------- */

    function buildQuote(model) {
      var selected = pumps.filter(function (pump) {
        return pump.model === state.selectedModel;
      })[0];

      if (!selected) return null;

      var delivered = model.isSized ? flowAtHead(selected.curve, model.totalHead) : 0;

      var panelWatt = state.panelWatt || selected.panelWatt;
      var panelCount = state.panelCount === null ? selected.panelCount : state.panelCount;
      var panelOption =
        panelOptions.filter(function (option) {
          return option.watt === panelWatt;
        })[0] || panelOptions[0];

      var cableLengthRaw = field('cableLength').value;
      var cableLength = cableLengthRaw !== '' ? toNumber(cableLengthRaw) : model.depth;
      var autoSize = autoCableSize(selected.cableTable, cableLength);
      var cableSize = state.cableSizeOverride !== null ? state.cableSizeOverride : autoSize;
      var cableOption =
        cableOptions.filter(function (option) {
          return option.size === cableSize;
        })[0] || null;
      var cableIsPOR = cableOption ? cableOption.price === null : false;
      var cableCost = cableOption && cableOption.price !== null ? cableOption.price * cableLength : 0;

      var solarCableLength = toNumber(field('solarCableLength').value);
      /* Two colours — red and black — so the run is charged twice */
      var solarCableCost = config.solarCablePricePerM * 2 * solarCableLength;

      var pumpCost = selected.price * state.pumpQty;
      var panelCost = (panelOption ? panelOption.price : 0) * panelCount;

      return {
        pump: selected,
        delivered: delivered,
        panelWatt: panelWatt,
        panelCount: panelCount,
        panelCost: panelCost,
        cableLength: cableLength,
        autoSize: autoSize,
        cableSize: cableSize,
        cableIsPOR: cableIsPOR,
        cableCost: cableCost,
        solarCableLength: solarCableLength,
        solarCableCost: solarCableCost,
        pumpCost: pumpCost,
        total: pumpCost + panelCost + cableCost + solarCableCost,
      };
    }

    /* ---------- daily yield from provincial sun hours ---------- */

    function dailyYield(model, quote) {
      var province =
        provinces.filter(function (item) {
          return item.name === provinceSelect.value;
        })[0] || provinces[0];

      if (!province) return null;

      var deliveredM3h = quote ? quote.delivered : model.bestFit ? model.bestFit.delivered : 0;
      var litresPerHour = deliveredM3h * 1000;
      if (litresPerHour <= 0) return null;

      return {
        province: province.name,
        winter: Math.round(litresPerHour * province.winter),
        summer: Math.round(litresPerHour * province.summer),
      };
    }

    /* ============================================================
       Rendering
       ============================================================ */

    function renderHeadBreakdown(model) {
      var panel = out('headPanel');
      show(panel, model.depth > 0 || model.litresPerHour > 0);

      setText('m3h', model.demandM3h > 0 ? '= ' + model.demandM3h.toFixed(2) + ' m³/h needed' : '');
      show(out('m3h'), model.demandM3h > 0);

      setText('breakdownDepth', model.depth.toFixed(0) + ' m');
      setText('breakdownRise', model.rise.toFixed(0) + ' m');
      setText('breakdownFrictionLabel', 'Pipe friction (' + model.pipeSize + ' mm pipe, ' + model.pipeRun.toFixed(0) + ' m)');
      setText('breakdownFriction', model.friction.toFixed(1) + ' m');

      field('headOverride').placeholder = model.calculatedHead.toFixed(1);
      show(out('headReset'), field('headOverride').value !== '');

      setText(
        'headSummary',
        'Estimated head ≈ ' +
          model.totalHead.toFixed(1) +
          ' m (static ' +
          (model.depth + model.rise).toFixed(1) +
          ' m + pipe friction ' +
          model.friction.toFixed(1) +
          ' m through ' +
          model.pipeSize +
          ' mm pipe).'
      );
      show(out('headSummary'), model.isSized);
    }

    function renderResult(model, quote) {
      var panel = out('resultPanel');
      show(panel, model.isSized);
      if (!model.isSized) return;

      var found = out('resultFound');
      var missing = out('resultMissing');

      show(found, !!model.bestFit);
      show(missing, !model.bestFit);

      if (!model.bestFit) {
        setText(
          'resultMissingText',
          "We'll need to assess this one — no standard pump delivers " +
            formatInt(model.litresPerHour) +
            ' L/h at about ' +
            model.totalHead.toFixed(0) +
            ' m (our largest unit reaches ' +
            maxPumpHead +
            ' m). Submit your details and our team will advise.'
        );
        return;
      }

      var best = model.bestFit;
      setText(
        'resultKicker',
        'Indicative result · need ' + formatInt(model.litresPerHour) + ' L/h @ ' + model.totalHead.toFixed(0) + ' m'
      );
      setText('resultModel', best.pump.model);
      setText(
        'resultDelivers',
        'Delivers about ' + formatInt(best.delivered * 1000) + ' L/h at this head — covers what you need.'
      );
      setText('resultPrice', formatRand(best.pump.price));

      var yieldData = dailyYield(model, quote);
      show(out('resultYield'), !!yieldData);
      if (yieldData) {
        setText('yieldHeading', 'Daily water yield — ' + yieldData.province);
        setText('yieldWinter', formatInt(yieldData.winter) + ' L/day');
        setText('yieldSummer', 'winter (worst case) · up to ' + formatInt(yieldData.summer) + ' L/day in summer');
        setText(
          'yieldNote',
          'Based on average peak sun hours for ' +
            yieldData.province +
            '. Actual output varies with weather, panel angle, and cloud cover.'
        );
      }
    }

    function renderPumpList(model) {
      var listNode = out('pumpList');
      if (!listNode) return;

      var visible = model.isSized && !state.showAllPumps ? model.capable : model.evaluated;

      setText(
        'pumpListHeading',
        model.isSized && state.selectedModel
          ? 'Prefer a different pump?'
          : model.isSized
          ? model.capable.length + ' pump' + (model.capable.length === 1 ? '' : 's') + ' deliver enough'
          : 'All pumps'
      );

      var toggle = out('pumpListToggle');
      show(toggle, model.isSized);
      if (toggle) {
        toggle.textContent = state.showAllPumps ? 'Hide the rest' : 'Show all ' + pumps.length;
      }

      listNode.innerHTML = visible
        .map(function (entry) {
          var pump = entry.pump;
          var isSelected = pump.model === state.selectedModel;
          var isBest = model.bestFit && pump.model === model.bestFit.pump.model;
          var unavailable = entry.status === 'no';
          var fillPercent =
            model.isSized && model.demandM3h > 0
              ? Math.min(entry.delivered / model.demandM3h, 1) * 100
              : 0;

          var meter = '';
          if (model.isSized) {
            meter = unavailable
              ? '<p class="pumpcalc-card__flag">' + entry.reason + '</p>'
              : '<div class="pumpcalc-meter"><span class="pumpcalc-meter__fill pumpcalc-meter__fill--' +
                entry.status +
                '" style="width:' +
                fillPercent +
                '%"></span></div>' +
                '<div class="pumpcalc-card__meter-row">' +
                '<span class="pumpcalc-card__delivery pumpcalc-card__delivery--' +
                entry.status +
                '">≈ ' +
                formatInt(entry.delivered * 1000) +
                ' L/h (' +
                entry.delivered.toFixed(2) +
                ' m³/h) at ' +
                model.totalHead.toFixed(0) +
                ' m</span>' +
                '<span class="pumpcalc-card__verdict">' +
                (entry.status === 'short' ? 'short of demand' : 'covers demand') +
                '</span>' +
                '</div>';
          }

          return (
            '<article class="pumpcalc-card' +
            (isSelected ? ' is-selected' : '') +
            (unavailable ? ' is-unavailable' : '') +
            (isBest ? ' has-badge' : '') +
            '">' +
            (isBest ? '<span class="pumpcalc-card__badge">Best fit</span>' : '') +
            '<div class="pumpcalc-card__body">' +
            '<div class="pumpcalc-card__info">' +
            '<h3 class="pumpcalc-card__model">' +
            pump.model +
            '</h3>' +
            '<p class="pumpcalc-card__spec">' +
            [
              pump.drive,
              pump.watts + ' W',
              'max ' + pump.maxFlow + ' m³/h',
              'max ' + pump.maxHead + ' m',
              pump.outlet,
              'panels ' + pump.panelDescription,
            ].join(' · ') +
            '</p>' +
            meter +
            '</div>' +
            '<div class="pumpcalc-card__buy">' +
            '<p class="pumpcalc-card__price">' +
            formatRand(pump.price) +
            '</p>' +
            '<p class="pumpcalc-card__vat">VAT incl</p>' +
            (unavailable
              ? ''
              : '<button type="button" class="pumpcalc-card__select' +
                (isSelected ? ' is-active' : '') +
                '" data-select-pump="' +
                pump.model.replace(/"/g, '&quot;') +
                '">' +
                (isSelected ? 'Selected' : 'Select') +
                '</button>') +
            '</div>' +
            '</div>' +
            '</article>'
          );
        })
        .join('');
    }

    function renderQuote(model, quote) {
      var panel = out('quotePanel');
      show(panel, !!quote);
      if (!quote) return;

      setText('quoteModel', quote.pump.model);

      var covers = quote.delivered >= model.demandM3h;
      var verdict = out('quoteVerdict');
      show(verdict, model.isSized);
      if (verdict) {
        verdict.classList.toggle('is-tight', !covers);
        setText(
          'quoteVerdict',
          'This pump will deliver ≈ ' +
            formatInt(quote.delivered * 1000) +
            ' L/h at ' +
            model.totalHead.toFixed(0) +
            ' m ' +
            (covers
              ? '— covers the ' + formatInt(model.litresPerHour) + ' L/h asked for.'
              : '— short of the ' + formatInt(model.litresPerHour) + ' L/h asked for.')
        );
      }

      setText('pumpQty', String(state.pumpQty));
      setText('quotePumpCost', formatRand(quote.pumpCost));

      setText('panelLabel', 'Solar panels (suggested ' + quote.pump.panelDescription + ')');
      panelSelect.value = String(quote.panelWatt);
      setText('panelCount', String(quote.panelCount));
      setText('quotePanelCost', formatRand(quote.panelCost));

      if (quote.cableSize !== null) cableSelect.value = String(quote.cableSize);
      field('cableLength').placeholder = model.depth ? String(model.depth) : 'm';
      setText('quoteCableCost', quote.cableIsPOR ? 'on request' : formatRand(quote.cableCost));
      setText(
        'cableNote',
        'Auto-sized ' +
          (quote.autoSize ? quote.autoSize + ' mm²' : '—') +
          ' for a ' +
          (quote.cableLength || 0) +
          ' m run' +
          (quote.cableIsPOR ? ' · 16 mm² price on request (not on price list)' : '')
      );
      show(out('cableReset'), field('cableLength').value !== '');

      setText(
        'solarCableNote',
        'm per colour · R' + config.solarCablePricePerM + '/m each'
      );
      setText('quoteSolarCableCost', formatRand(quote.solarCableCost));

      setText('quoteTotal', formatRand(quote.total));
      show(out('quotePorNote'), quote.cableIsPOR);
    }

    /* ---------- WhatsApp hand-off ---------- */

    function buildWhatsAppLink(model, quote) {
      var name = field('name').value.trim();
      var phone = field('phone').value.trim();
      var email = field('email').value.trim();
      var town = field('town').value.trim();
      var notes = field('notes').value.trim();
      var province = provinceSelect.value;

      var chosen = quote ? quote.pump : model.bestFit ? model.bestFit.pump : null;
      var delivered = quote ? quote.delivered : model.bestFit ? model.bestFit.delivered : 0;

      /* Without an explicit selection, quote the best fit at its default kit */
      var total = quote
        ? quote.total
        : model.bestFit
        ? model.bestFit.pump.price +
          defaultPanelPrice(model.bestFit.pump) * model.bestFit.pump.panelCount +
          config.solarCablePricePerM * 2 * config.solarCableDefaultM
        : 0;

      var yieldData = dailyYield(model, quote);

      var lines = [
        '☀️ *AGRI SOLSEC — Solar Pump Quote Request*',
        '━━━━━━━━━━━━━━━━━━━━',
        '',
        '👤 *Customer*',
        '• Name: ' + (name || '—'),
        '• Phone / WhatsApp: ' + (phone || '—'),
        email ? '• Email: ' + email : null,
        town ? '• Town / farm: ' + town : null,
        province ? '• Province: ' + province : null,
        '',
        '🕳️ *Borehole & site*',
        '• Borehole depth: ' + (model.depth || 0) + ' m',
        '• Delivery distance: ' + (model.distance || 0) + ' m',
        '• Rise to tank: ' + (model.rise || 0) + ' m',
        '• Pipe size: ' + model.pipeSize + ' mm',
        '• Water needed: ' + formatInt(model.litresPerHour) + ' L/h',
        model.isSized ? '• Pipe friction head: ' + model.friction.toFixed(1) + ' m' : null,
        model.isSized ? '• Estimated *total head*: ' + model.totalHead.toFixed(1) + ' m' : null,
        '',
        '🔧 *Suggested pump*',
        chosen ? '• Model: ' + chosen.model : '• Needs assessment',
        chosen ? '• Drive: ' + chosen.drive + ' · ' + chosen.watts + ' W' : null,
        chosen ? '• Outlet: ' + chosen.outlet : null,
        chosen
          ? '• Delivers ≈ ' + formatInt(delivered * 1000) + ' L/h at ' + model.totalHead.toFixed(0) + ' m head'
          : null,
        '',
        chosen ? '☀️ *Solar panels*' : null,
        chosen ? buildPanelLine(chosen, quote) : null,
        '',
        quote ? '🔌 *Cable*' : null,
        quote && quote.cableSize
          ? '• DC cable: ' +
            quote.cableSize +
            ' mm² × ' +
            quote.cableLength +
            ' m' +
            (quote.cableIsPOR ? ' (price on request)' : '')
          : null,
        quote ? '• Solar cable (red + black): ' + quote.solarCableLength + ' m per colour' : null,
        '',
        '💧 *Estimated daily water yield*',
        yieldData ? '• Winter (worst case): ' + formatInt(yieldData.winter) + ' L/day' : '• Pending pump selection',
        yieldData ? '• Summer (best case): ' + formatInt(yieldData.summer) + ' L/day' : null,
        yieldData ? '• Based on ' + yieldData.province + ' sun hours' : null,
        '',
        '💰 *Indicative total (VAT incl)*',
        chosen ? '• ' + formatRand(total) : '• On request',
        '',
        notes ? '📝 *Notes from customer*' : null,
        notes || null,
        notes ? '' : null,
        '━━━━━━━━━━━━━━━━━━━━',
        'Please confirm sizing and send the formal quote. 🙏',
        '_Sent from the Agri Solsec quote page_',
      ];

      return (
        'https://wa.me/' +
        config.whatsappNumber +
        '?text=' +
        encodeURIComponent(
          lines
            .filter(function (line) {
              return line !== null;
            })
            .join('\n')
        )
      );
    }

    function defaultPanelPrice(pump) {
      var option =
        panelOptions.filter(function (item) {
          return item.watt === pump.panelWatt;
        })[0] || panelOptions[0];
      return option ? option.price : 0;
    }

    function buildPanelLine(pump, quote) {
      var count = quote ? quote.panelCount : pump.panelCount;
      var watt = quote ? quote.panelWatt : pump.panelWatt;
      return '• ' + count + ' × ' + watt + ' W panel' + (count > 1 ? 's' : '');
    }

    /* ---------- downloadable PDF quote ---------- */

    /* Stable for the life of the page so re-downloads keep one reference */
    var quoteRef = (function () {
      var now = new Date();
      var stamp =
        String(now.getFullYear()).slice(2) +
        ('0' + (now.getMonth() + 1)).slice(-2) +
        ('0' + now.getDate()).slice(-2);
      return 'AS-' + stamp + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
    })();

    function quoteDate() {
      var now = new Date();
      try {
        return now.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
      } catch (error) {
        return now.toDateString();
      }
    }

    function buildPdfData(model, quote) {
      var yieldData = dailyYield(model, quote);
      var panelPrice =
        (panelOptions.filter(function (option) {
          return option.watt === quote.panelWatt;
        })[0] || panelOptions[0] || { price: 0 }).price;

      var lines = [
        {
          label: 'Solar pump — ' + quote.pump.model,
          detail:
            state.pumpQty +
            ' × ' +
            formatRand(quote.pump.price) +
            ' · ' +
            quote.pump.drive +
            ' · ' +
            quote.pump.watts +
            ' W · outlet ' +
            quote.pump.outlet,
          amount: formatRand(quote.pumpCost),
        },
        {
          label: 'Solar panels',
          detail:
            quote.panelCount +
            ' × ' +
            quote.panelWatt +
            ' W at ' +
            formatRand(panelPrice) +
            ' each (suggested ' +
            quote.pump.panelDescription +
            ')',
          amount: formatRand(quote.panelCost),
        },
        {
          label: 'Borehole (motor) cable',
          detail:
            (quote.cableSize ? quote.cableSize + ' mm² × ' : '') +
            quote.cableLength +
            ' m run' +
            (quote.autoSize ? ' · auto-sized ' + quote.autoSize + ' mm²' : ''),
          amount: quote.cableIsPOR ? 'On request' : formatRand(quote.cableCost),
        },
        {
          label: 'Solar cable (red + black)',
          detail:
            quote.solarCableLength +
            ' m per colour at R' +
            config.solarCablePricePerM +
            '/m each',
          amount: formatRand(quote.solarCableCost),
        },
      ];

      return {
        ref: quoteRef,
        date: quoteDate(),
        customer: {
          name: field('name').value.trim(),
          phone: field('phone').value.trim(),
          email: field('email').value.trim(),
          town: field('town').value.trim(),
          province: provinceSelect.value,
        },
        site: {
          depth: model.depth || 0,
          distance: model.distance || 0,
          rise: model.rise || 0,
          pipeSize: model.pipeSize,
          litresPerHour: formatInt(model.litresPerHour),
          friction: model.isSized ? model.friction.toFixed(1) : null,
          totalHead: model.isSized ? model.totalHead.toFixed(1) : null,
        },
        pump: {
          model: quote.pump.model,
          drive: quote.pump.drive,
          watts: quote.pump.watts,
          outlet: quote.pump.outlet,
          delivers: model.isSized
            ? '≈ ' +
              formatInt(quote.delivered * 1000) +
              ' L/h at ' +
              model.totalHead.toFixed(0) +
              ' m head'
            : null,
        },
        yield: yieldData
          ? {
              province: yieldData.province,
              winter: formatInt(yieldData.winter),
              summer: formatInt(yieldData.summer),
            }
          : null,
        lines: lines,
        total: formatRand(quote.total),
        porNote: quote.cableIsPOR,
        notes: field('notes').value.trim(),
        disclaimer: config.pdfDisclaimer,
        company: {
          name: config.companyName,
          phone: config.companyPhone,
          email: config.companyEmail,
          website: config.companyWebsite,
          address: config.companyAddress,
          footer: config.companyFooter,
        },
      };
    }

    function downloadPdf() {
      if (!window.AgriPumpQuotePdf) return;

      var model = compute();
      var quote = buildQuote(model);
      if (!quote) return;

      var blob = window.AgriPumpQuotePdf.generate(buildPdfData(model, quote));
      var url = URL.createObjectURL(blob);
      var customer = field('name').value.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
      var link = document.createElement('a');

      link.href = url;
      link.download = 'Agri-Solsec-Quote-' + (customer ? customer + '-' : '') + quoteRef + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 2000);
    }

    /* ============================================================
       Main render loop
       ============================================================ */

    function render() {
      var model = compute();

      /* Adopt the best fit automatically until the user picks one themselves */
      if (model.isSized && model.bestFit && !state.manualPick) {
        if (state.selectedModel !== model.bestFit.pump.model) {
          state.selectedModel = model.bestFit.pump.model;
          state.panelWatt = model.bestFit.pump.panelWatt;
          state.panelCount = model.bestFit.pump.panelCount;
          state.cableSizeOverride = null;
        }
      }

      var quote = buildQuote(model);

      renderHeadBreakdown(model);
      renderResult(model, quote);
      renderPumpList(model);
      renderQuote(model, quote);

      var canSend = field('name').value.trim() !== '' && field('phone').value.trim() !== '';
      var sendButton = out('sendButton');
      if (sendButton) {
        sendButton.disabled = !canSend;
        sendButton.dataset.href = canSend ? buildWhatsAppLink(model, quote) : '';
      }
    }

    /* ============================================================
       Events
       ============================================================ */

    root.addEventListener('input', render);
    root.addEventListener('change', render);

    root.addEventListener('click', function (event) {
      var target = event.target.closest('button');
      if (!target || !root.contains(target)) return;

      if (target.hasAttribute('data-select-pump')) {
        state.selectedModel = target.getAttribute('data-select-pump');
        state.manualPick = true;
        var picked = pumps.filter(function (pump) {
          return pump.model === state.selectedModel;
        })[0];
        if (picked) {
          state.panelWatt = picked.panelWatt;
          state.panelCount = picked.panelCount;
        }
        state.cableSizeOverride = null;
        render();
        return;
      }

      if (target.hasAttribute('data-step')) {
        var delta = toNumber(target.getAttribute('data-delta'));
        if (target.getAttribute('data-step') === 'pumpQty') {
          state.pumpQty = Math.max(1, state.pumpQty + delta);
        } else {
          var current = state.panelCount === null ? 0 : state.panelCount;
          state.panelCount = Math.max(0, current + delta);
        }
        render();
        return;
      }

      if (target.hasAttribute('data-toggle-pumps')) {
        state.showAllPumps = !state.showAllPumps;
        render();
        return;
      }

      if (target.hasAttribute('data-reset-head')) {
        field('headOverride').value = '';
        render();
        return;
      }

      if (target.hasAttribute('data-reset-cable')) {
        field('cableLength').value = '';
        state.cableSizeOverride = null;
        render();
        return;
      }

      if (target.hasAttribute('data-download-pdf')) {
        downloadPdf();
        return;
      }

      if (target.hasAttribute('data-send')) {
        var href = target.dataset.href;
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      }
    });

    panelSelect.addEventListener('change', function () {
      state.panelWatt = toNumber(panelSelect.value);
    });

    cableSelect.addEventListener('change', function () {
      state.cableSizeOverride = toNumber(cableSelect.value);
    });

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pump-calculator]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor re-renders sections in place */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
